import React, { useState } from "react";
import {
  User,
  MapPin,
  Briefcase,
  GraduationCap,
  FileText,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";

import JobseekerService from "./services/JobseekerService";
import { useNotification } from "./CustomNotificationSystem";

const JobseekerRegisterForm = ({ jobId, onComplete }) => {
  const [formData, setFormData] = useState({
    name: "",
    avatar: null,
    jobTitle: "",
    location: "",
    postcode: "",
    latitude: "",
    longitude: "",
    city: "",
    email: "",
    phone: "",
    education: [{ degree: "", institution: "", year: "" }],
    workExperience: [
      { position: "", company: "", duration: "", responsibilities: [""] },
    ],
    skills: [""],
    cv: null,
    job: jobId,
  });

  const [errors, setErrors] = useState({});
  const notify = useNotification();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleEducationChange = (index, field, value) => {
    const updatedEducation = [...formData.education];
    updatedEducation[index][field] = value;
    setFormData((prev) => ({ ...prev, education: updatedEducation }));
  };

  const addEducation = () => {
    setFormData((prev) => ({
      ...prev,
      education: [...prev.education, { degree: "", institution: "", year: "" }],
    }));
  };

  const removeEducation = (index) => {
    if (formData.education.length > 1) {
      setFormData((prev) => ({
        ...prev,
        education: prev.education.filter((_, i) => i !== index),
      }));
    }
  };

  const handleWorkExperienceChange = (index, field, value) => {
    const updatedWork = [...formData.workExperience];
    updatedWork[index][field] = value;
    setFormData((prev) => ({ ...prev, workExperience: updatedWork }));
  };

  const handleResponsibilityChange = (workIndex, respIndex, value) => {
    const updatedWork = [...formData.workExperience];
    updatedWork[workIndex].responsibilities[respIndex] = value;
    setFormData((prev) => ({ ...prev, workExperience: updatedWork }));
  };

  const addResponsibility = (workIndex) => {
    const updatedWork = [...formData.workExperience];
    updatedWork[workIndex].responsibilities.push("");
    setFormData((prev) => ({ ...prev, workExperience: updatedWork }));
  };

  const removeResponsibility = (workIndex, respIndex) => {
    const updatedWork = [...formData.workExperience];
    if (updatedWork[workIndex].responsibilities.length > 1) {
      updatedWork[workIndex].responsibilities = updatedWork[
        workIndex
      ].responsibilities.filter((_, i) => i !== respIndex);
      setFormData((prev) => ({ ...prev, workExperience: updatedWork }));
    }
  };

  const addWorkExperience = () => {
    setFormData((prev) => ({
      ...prev,
      workExperience: [
        ...prev.workExperience,
        { position: "", company: "", duration: "", responsibilities: [""] },
      ],
    }));
  };

  const removeWorkExperience = (index) => {
    if (formData.workExperience.length > 1) {
      setFormData((prev) => ({
        ...prev,
        workExperience: prev.workExperience.filter((_, i) => i !== index),
      }));
    }
  };

  const handleSkillChange = (index, value) => {
    const updatedSkills = [...formData.skills];
    updatedSkills[index] = value;
    setFormData((prev) => ({ ...prev, skills: updatedSkills }));
  };

  const addSkill = () => {
    setFormData((prev) => ({ ...prev, skills: [...prev.skills, ""] }));
  };

  const removeSkill = (index) => {
    if (formData.skills.length > 1) {
      setFormData((prev) => ({
        ...prev,
        skills: prev.skills.filter((_, i) => i !== index),
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (allowedTypes.includes(file.type)) {
        setFormData((prev) => ({ ...prev, cv: file }));
        setErrors((prev) => ({ ...prev, cv: "" }));
      } else {
        setErrors((prev) => ({
          ...prev,
          cv: "Please upload a PDF, DOC, or DOCX file",
        }));
      }
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.includes("image")) {
        setFormData((prev) => ({ ...prev, avatar: file }));
        setErrors((prev) => ({ ...prev, avatar: "" }));
      } else {
        setErrors((prev) => ({
          ...prev,
          avatar: "Please upload an image file",
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Email is invalid";

    if (!formData.jobTitle.trim()) newErrors.jobTitle = "Job title is required";
    if (!formData.location.trim()) newErrors.location = "Location is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";

    // Check if at least one education entry is filled

    /*
     * remving this validation for now
      const hasEducation = formData.education.some(
        (edu) => edu.degree.trim() || edu.institution.trim() || edu.year.trim(),
      );
    if (!hasEducation)
      newErrors.education = "At least one education entry is required";
      */

    // Check if at least one work experience is filled
    /*
    const hasWork = formData.workExperience.some(
      (work) =>
        work.position.trim() || work.company.trim() || work.duration.trim(),
    );
    if (!hasWork)
      newErrors.workExperience = "At least one work experience is required";
    */

    // Check if at least one skill is filled
    /*
    const hasSkills = formData.skills.some((skill) => skill.trim());
    if (!hasSkills) newErrors.skills = "At least one skill is required";
    */

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Clean up empty entries
      const cleanedData = {
        ...formData,
        education: formData.education.filter(
          (edu) =>
            edu.degree.trim() || edu.institution.trim() || edu.year.trim(),
        ),
        workExperience: formData.workExperience
          .filter(
            (work) =>
              work.position.trim() ||
              work.company.trim() ||
              work.duration.trim(),
          )
          .map((work) => ({
            ...work,
            responsibilities: work.responsibilities.filter((resp) =>
              resp.trim(),
            ),
          })),
        skills: formData.skills.filter((skill) => skill.trim()),
      };
      try {
        await JobseekerService.registerToJob(cleanedData);
      } catch (error) {
        const response = error.response.data;
        notify(response.message, "error");
        return false;
      }
      if (onComplete) {
        onComplete(jobId);
      }
      //console.log("Form submitted:", cleanedData);
      notify("Form submitted successfully!", "success");
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-3 sm:p-4 md:p-6 bg-white min-h-screen">
      <div className="bg-indigo-600 text-white p-4 sm:p-5 md:p-6 rounded-lg mb-6 md:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2 sm:gap-3">
          <User className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0" />
          <span>Add Jobseeker</span>
        </h1>
        <p className="mt-2 text-sm sm:text-base text-blue-100">Fill in jobseeker details to add</p>
      </div>

      <div className="space-y-6 md:space-y-8">
        {/* Personal Information */}
        <div className="bg-gray-50 p-4 sm:p-5 md:p-6 rounded-lg">
          <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600 flex-shrink-0" />
            Personal Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter full name"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Avatar
              </label>
              <input
                type="file"
                name="avatar"
                onChange={handleAvatarChange}
                accept="image/*"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.avatar && (
                <p className="text-red-500 text-sm mt-1">{errors.avatar}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Title *
              </label>
              <input
                type="text"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g. Software Engineer"
              />
              {errors.jobTitle && (
                <p className="text-red-500 text-sm mt-1">{errors.jobTitle}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="your.email@example.com"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="+1 (555) 123-4567"
              />
              {errors.phone && (
                <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
              )}
            </div>
          </div>
        </div>

        {/* Location Information */}
        <div className="bg-gray-50 p-4 sm:p-5 md:p-6 rounded-lg">
          <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0" />
            Location Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location *
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter location"
              />
              {errors.location && (
                <p className="text-red-500 text-sm mt-1">{errors.location}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postcode
              </label>
              <input
                type="text"
                name="postcode"
                value={formData.postcode}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="12345"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="City name"
              />
            </div>

            {/*
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Latitude
              </label>
              <input
                type="text"
                name="latitude"
                value={formData.latitude}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="40.7128"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Longitude
              </label>
              <input
                type="text"
                name="longitude"
                value={formData.longitude}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="-74.0060"
              />
            </div>
            */}
          </div>
        </div>

        {/* Education */}
        <div className="bg-gray-50 p-4 sm:p-5 md:p-6 rounded-lg">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 sm:mb-4 gap-3">
            <h2 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-blue-600 flex-shrink-0" />
              Education
            </h2>
            <button
              type="button"
              onClick={addEducation}
              className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors min-h-[44px] w-full sm:w-auto text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Add Education</span>
            </button>
          </div>
          {errors.education && (
            <p className="text-red-500 text-sm mb-4">{errors.education}</p>
          )}

          {formData.education.map((edu, index) => (
            <div
              key={index}
              className="border border-gray-200 p-4 rounded-md mb-4 bg-white"
            >
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium text-gray-700">
                  Education {index + 1}
                </h3>
                {formData.education.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeEducation(index)}
                    className="text-red-500 hover:text-red-700 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  placeholder="Degree"
                  value={edu.degree}
                  onChange={(e) =>
                    handleEducationChange(index, "degree", e.target.value)
                  }
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="Institution"
                  value={edu.institution}
                  onChange={(e) =>
                    handleEducationChange(index, "institution", e.target.value)
                  }
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="Year"
                  value={edu.year}
                  onChange={(e) =>
                    handleEducationChange(index, "year", e.target.value)
                  }
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Work Experience */}
        <div className="bg-gray-50 p-4 sm:p-5 md:p-6 rounded-lg">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 sm:mb-4 gap-3">
            <h2 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600 flex-shrink-0" />
              Work Experience
            </h2>
            <button
              type="button"
              onClick={addWorkExperience}
              className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors min-h-[44px] w-full sm:w-auto text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Add Experience</span>
            </button>
          </div>
          {errors.workExperience && (
            <p className="text-red-500 text-sm mb-4">{errors.workExperience}</p>
          )}

          {formData.workExperience.map((work, workIndex) => (
            <div
              key={workIndex}
              className="border border-gray-200 p-4 rounded-md mb-4 bg-white"
            >
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium text-gray-700">
                  Experience {workIndex + 1}
                </h3>
                {formData.workExperience.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeWorkExperience(workIndex)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <input
                  type="text"
                  placeholder="Position"
                  value={work.position}
                  onChange={(e) =>
                    handleWorkExperienceChange(
                      workIndex,
                      "position",
                      e.target.value,
                    )
                  }
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="Company"
                  value={work.company}
                  onChange={(e) =>
                    handleWorkExperienceChange(
                      workIndex,
                      "company",
                      e.target.value,
                    )
                  }
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="Duration (e.g., 2020-2022)"
                  value={work.duration}
                  onChange={(e) =>
                    handleWorkExperienceChange(
                      workIndex,
                      "duration",
                      e.target.value,
                    )
                  }
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Responsibilities
                  </label>
                  <button
                    type="button"
                    onClick={() => addResponsibility(workIndex)}
                    className="text-blue-500 hover:text-blue-700 text-sm flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    Add
                  </button>
                </div>
                {work.responsibilities.map((resp, respIndex) => (
                  <div key={respIndex} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Responsibility"
                      value={resp}
                      onChange={(e) =>
                        handleResponsibilityChange(
                          workIndex,
                          respIndex,
                          e.target.value,
                        )
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {work.responsibilities.length > 1 && (
                      <button
                        type="button"
                        onClick={() =>
                          removeResponsibility(workIndex, respIndex)
                        }
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Skills */}
        <div className="bg-gray-50 p-4 sm:p-5 md:p-6 rounded-lg">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 sm:mb-4 gap-3">
            <h2 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
              Skills
            </h2>
            <button
              type="button"
              onClick={addSkill}
              className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors min-h-[44px] w-full sm:w-auto text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Add Skill</span>
            </button>
          </div>
          {errors.skills && (
            <p className="text-red-500 text-sm mb-4">{errors.skills}</p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {formData.skills.map((skill, index) => (
              <div key={index} className="flex gap-1">
                <input
                  type="text"
                  placeholder="Skill"
                  value={skill}
                  onChange={(e) => handleSkillChange(index, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-1/3"
                />
                {formData.skills.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSkill(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CV Upload */}
        <div className="bg-gray-50 p-4 sm:p-5 md:p-6 rounded-lg">
          <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600 flex-shrink-0" />
            Upload CV
          </h2>
          <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              className="hidden"
              id="cv-upload"
            />
            <label
              htmlFor="cv-upload"
              className="cursor-pointer flex flex-col items-center gap-2"
            >
              <Upload className="w-8 h-8 text-gray-400" />
              <span className="text-gray-600">
                {formData.cv
                  ? formData.cv.name
                  : "Click to upload CV (PDF, DOC, DOCX)"}
              </span>
              <span className="text-sm text-gray-400">Max file size: 10MB</span>
            </label>
            {errors.cv && (
              <p className="text-red-500 text-sm mt-2">{errors.cv}</p>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center pt-4 md:pt-6">
          <button
            type="submit"
            onClick={handleSubmit}
            className="bg-indigo-500 text-white px-6 py-2.5 sm:px-8 sm:py-3 rounded-lg font-semibold hover:bg-indigo-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 w-full sm:w-auto min-h-[44px] text-sm sm:text-base"
          >
            Submit Application
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobseekerRegisterForm;
